import {steveDataStorage} from './sdk-helpers';
import DashboardService from '../services/dashboard-service';
import UserService from '../services/user-service';
import {replaceBRWithNewLine} from './text';
import {getFormattedDate} from './dateTime';

/**
 * Old approach had widget filter saved as string in DB
 * This method checks filter and parses it if needed
 */
const mapWidgetConfig = (config) => {
  const newConfig = {
    ...config,
  };

  if (typeof newConfig.filter === 'string') {
    newConfig.filter = JSON.parse(newConfig.filter);

    if (typeof newConfig.filter.mongoQuery === 'string') {
      newConfig.filter.mongoQuery = JSON.parse(newConfig.filter.mongoQuery);
    }

    if (typeof newConfig.filter.humanQuery === 'string') {
      newConfig.filter.humanQuery = JSON.parse(newConfig.filter.humanQuery);
    }
  }

  return newConfig;
};

export const getMappedWidgetTagsForDashboard = (tagIds, widgetTags) =>
  tagIds
    .map((id) => Object.values(widgetTags).find((tag) => tag.id === id))
    .filter(
      (tag) => tag && tag.title.toLowerCase() !== 'new' && tag.title.toLowerCase() !== 'generic'
    );

export function parseDashboard({dashboard, isFavorite}, widgetTypes, widgetTypeTags) {
  if (dashboard) {
    const widgets = dashboard.getWidgets();
    const parsedLayouts = {};
    const parsedWidgets = {};

    Object.keys(widgets).forEach((widgetId) => {
      let defaultH = 0;
      let defaultW = 0;
      let minH = 0;
      let minW = 0;
      let maxH = 0;
      let maxW = 0;
      if (widgetTypes && widgetTypes.length > 0) {
        const widgetTypeId = widgets[widgetId].typeId;
        const widgetType = widgetTypes.find((el) => el.id === widgetTypeId);
        defaultH = widgetType.settings.DefaultH;
        defaultW = widgetType.settings.DefaultW;
        minH = widgetType ? widgetType.settings.MinH : 1;
        minW = widgetType ? widgetType.settings.MinW : 1;
        maxH = widgetType ? widgetType.settings.MaxH : 3;
        maxW = widgetType ? widgetType.settings.MaxW : 3;
      }
      parsedWidgets[widgetId] = {
        ...widgets[widgetId],
        title: widgets[widgetId].title || '',
        config: mapWidgetConfig(widgets[widgetId].config),
        w: defaultW,
        h: defaultH,
        x: 0,
        y: 0,
        id: widgetId,
      };
      Object.keys(widgets[widgetId].layouts).forEach((layout) => {
        if (!parsedLayouts[layout]) {
          parsedLayouts[layout] = [];
        }
        if (Object.keys(widgets[widgetId].layouts[layout]).length) {
          parsedLayouts[layout].push({
            i: widgetId,
            x: widgets[widgetId].layouts[layout].x,
            y: widgets[widgetId].layouts[layout].y,
            w: widgets[widgetId].layouts[layout].w || defaultW,
            h: widgets[widgetId].layouts[layout].h || defaultH,
            minH,
            maxH,
            minW,
            maxW,
          });
        }
      });
    });
    const widgetTitleList = Object.values(parsedWidgets).map((widget) => widget.title);
    const dashboardWidgetTagsIds = [...new Set(dashboard.getWidgetTagIds())];
    const parsedDashboardTags = getMappedWidgetTagsForDashboard(
      dashboardWidgetTagsIds,
      widgetTypeTags
    );

    return {
      title: dashboard.getName(),
      delete: () => dashboard.delete(),
      description: replaceBRWithNewLine(dashboard.getDescription()),
      id: dashboard.getId(),
      ownerId: dashboard.getOwnerId(),
      createdBy: dashboard.getCreatedBy(),
      createdOn: getFormattedDate(dashboard.getCreatedOn()),
      createdIn: dashboard.getCreatedInOrgName(),
      tenantId: dashboard.getTenantId(),
      widgetsMap: parsedWidgets,
      layouts: parsedLayouts,
      modalFormData: {
        dashboardName: dashboard.getName(),
        dashboardDescription: dashboard.getDescription(),
        selectedImageOptionId: dashboard.getImageOption(),
        selectedPermissionSettingsId: dashboard.getPrivate() ? 1 : 2,
        image: dashboard.getImage(),
        defaultImage: dashboard.getDefaultImage(),
        isShared: dashboard.getShared(),
        isFavorite,
      },
      tags: parsedDashboardTags,
      widgetTitleList,
    };
  }
  return dashboard;
}

export const mapDashboardData = ({modalFormData, widgetsMap, layouts}) => ({
  dashboardName: modalFormData.dashboardName,
  dashboardDescription: modalFormData.dashboardDescription,
  selectedImageOptionId: modalFormData.selectedImageOptionId,
  selectedPermissionSettingsId: modalFormData.selectedPermissionSettingsId,
  isFavorite: modalFormData.isFavorite,
  image: modalFormData.image,
  isShared: modalFormData.isShared,
  layouts,
  widgets: widgetsMap,
});

export function isAuthorizedAction(action) {
  // eslint-disable-next-line no-underscore-dangle
  const actions = window.DashboardComponent._userSession._userEntity.authorizedActions;

  return actions ? actions.includes(action) : false;
}

export function isUserWithDashboardViewerRole() {
  // eslint-disable-next-line no-underscore-dangle
  return (
    window.DashboardComponent._userSession._userEntity.actionGroupId ===
    '68a298fa-f4ca-4c6a-886c-a096fc9251fc'
  );
}

export function isDashboardCreatedInCurrentOrganization(tenantId) {
  // eslint-disable-next-line no-underscore-dangle
  const currentTenantId = window.DashboardComponent._userSession._activeOrganization.id;

  return tenantId === currentTenantId;
}

export function updateFavoriteDashboardsOrder(dashboard, action) {
  const favoriteIds = steveDataStorage('dashboards', 'favorites', true).get('order') || [];
  let newFavoriteIds = null;
  switch (action) {
    case 'ADD':
      newFavoriteIds = [...favoriteIds, dashboard.id];
      break;
    case 'REMOVE':
      newFavoriteIds = favoriteIds.filter((favoriteId) => favoriteId !== dashboard.id);
      break;
    case 'REORDER':
      newFavoriteIds = dashboard.map(({id}) => id);
      break;
    default:
      newFavoriteIds = [];
  }

  steveDataStorage('dashboards', 'favorites', true).set(newFavoriteIds, 'order');
}

export function getOrderedFavoriteDashboards(favoriteDashboards) {
  const favoriteIds = steveDataStorage('dashboards', 'favorites', true).get('order') || [];
  return favoriteDashboards.sort((a, b) => {
    const aIndex = favoriteIds.indexOf(a.id);
    const bIndex = favoriteIds.indexOf(b.id);
    if (bIndex === -1) {
      return -1;
    }
    if (aIndex === -1) {
      return 1;
    }
    return aIndex - bIndex;
  });
}

export const saveAccordionsState = (value) =>
  steveDataStorage('dashboards', 'accordion', false).set(value, 'opened');

export const loadAccordionsState = () =>
  steveDataStorage('dashboards', 'accordion', false).get('opened', true);

export const getLatestViewInDashboards = () =>
  steveDataStorage('dashboards', 'latest', false).get('view', false);

// Can be either "library" or specific dashboard id
export const setLatestViewInDashboards = (value) =>
  steveDataStorage('dashboards', 'latest', false).set(value, 'view');

export const setRunningDashboardId = (id) =>
  steveDataStorage('dashboards', 'latest', false).set(id, 'running');

export async function getRunningDashboardId(favoriteDashboards) {
  const userRunningDashboardId = steveDataStorage('dashboards', 'latest', false).get(
    'running',
    false
  );
  const defaultRunningId =
    favoriteDashboards && favoriteDashboards.length
      ? getOrderedFavoriteDashboards(favoriteDashboards)[0].id
      : null;
  if (!userRunningDashboardId) {
    return defaultRunningId;
  }
  const dashboard = await DashboardService.getDashboard(userRunningDashboardId);
  if (!dashboard) {
    return defaultRunningId;
  }
  if (!dashboard.getPrivate() || dashboard.getOwnerId() === UserService.getLoggedUser().getId()) {
    return dashboard.id;
  }
  return defaultRunningId;
}

export function getWidgetListWithNoDuplicatesAndSorted(widgetTitleList) {
  if (!widgetTitleList.length) {
    return [];
  }
  return widgetTitleList.filter((value, index) => widgetTitleList.indexOf(value) === index).sort();
}

export const prepareDashboardDataForExport = (dashboard = {}) => {
  const {
    id,
    title: name,
    description,
    widgetsMap: widgets,
    modalFormData: {isShared: shared, selectedImageOptionId: imageOption, defaultImage} = {},
  } = dashboard;

  const dataForExport = {
    id,
    name,
    description,
    shared,
    widgets,
    imageOption,
    defaultImage,
  };

  return dataForExport;
};
